/** Extensible node definitions for character/story production workflows. */

import {
  CHARACTER_NODE_CATALOG,
  PORT_TYPES,
  canExecuteCatalogNode,
  characterPickerDefs,
  defaultPortsForType,
  getCatalogDef,
  missingRequiredPorts,
  portsCompatible,
} from './characterNodeCatalog';

export const NODE_CATEGORIES = [
  { id: 'inputs', label: 'Inputs' },
  { id: 'utilities', label: 'Utilities' },
  { id: 'processing', label: 'Processing' },
  { id: 'outputs', label: 'Outputs' },
];

/** Merge legacy registry entries with the authoritative character catalog. */
export const NODE_REGISTRY = {
  ...Object.fromEntries(
    Object.values(CHARACTER_NODE_CATALOG).map((def) => [
      def.type,
      {
        type: def.type,
        label: def.label,
        category: def.category,
        description: def.purpose,
        executable: Boolean(def.executable),
        operation: def.operation || undefined,
        ports: { inputs: true, outputs: (def.ports?.outputs || []).length > 0 },
        resultPresentation: def.resultPresentation,
        settings: def.settings || [],
        availability: def.availability,
        availabilityDetail: def.availabilityDetail,
        activity: def.activity,
        implementationVersion: def.implementationVersion,
        adminOnly: def.adminOnly,
        platformPermission: def.platformPermission,
        allowsMultipleInstances: def.allowsMultipleInstances !== false,
      },
    ])
  ),
  // Infrastructure test — excluded from character catalog picker.
  dev_worker: {
    type: 'dev_worker',
    label: 'Worker Dev Node',
    category: 'processing',
    description:
      'Administrator-only infrastructure test. Submits a deterministic worker job without calling external models.',
    executable: true,
    operation: 'dev_test',
    adminOnly: true,
    platformPermission: 'platform.dev_worker',
    ports: { inputs: true, outputs: true },
    resultPresentation: 'report',
    settings: [],
    availability: 'available',
  },
  action: {
    type: 'action',
    label: 'Action (legacy)',
    category: 'processing',
    description: 'Planned action from earlier builds. Prefer catalog operations.',
    executable: false,
    ports: { inputs: true, outputs: true },
    resultPresentation: 'planned',
    legacy: true,
    availability: 'unavailable',
  },
};

/** Labels used by the toolbar / inspector (includes legacy). */
export const NODE_TYPES = Object.fromEntries(
  Object.values(NODE_REGISTRY).map((def) => [def.type, def.label])
);

export const PICKER_NODE_TYPES = [
  ...characterPickerDefs({ includeUnavailable: true }).map((def) => NODE_REGISTRY[def.type]),
  // Infrastructure test — not in character catalog; shown only when admin filter allows.
  NODE_REGISTRY.dev_worker,
].filter(Boolean);

export function getNodeDef(type) {
  return NODE_REGISTRY[type] || NODE_REGISTRY.text;
}

export function isExecutableNode(type) {
  return Boolean(getNodeDef(type).executable);
}

export function newNode(type, index = 0, position = null) {
  const def = getNodeDef(type);
  const catalog = getCatalogDef(type);
  const settings = {};
  (def.settings || catalog?.settings || []).forEach((field) => {
    settings[field.key] = field.default;
  });
  const x = position?.x ?? 60 + (index % 4) * 300;
  const y = position?.y ?? 70 + Math.floor(index / 4) * 260;
  return {
    id: `node-${Date.now()}-${Math.random().toString(36).slice(2)}`,
    type,
    label: def.label,
    text: '',
    prompt: '',
    settings,
    implementation_version: catalog?.implementationVersion || '1',
    // Result approval: pin a specific succeeded run id for downstream use.
    approved: false,
    selected_run_id: '',
    x,
    y,
  };
}

export function starterGraph(brief) {
  const nodes = ['text', 'combine', 'create_images', 'output'].map((type, i) => newNode(type, i));
  nodes[0].label = 'Character brief';
  nodes[0].text = brief;
  nodes[1].label = 'Build the character';
  nodes[2].label = 'Generate Concepts';
  nodes[3].label = 'Character output';
  return {
    nodes,
    edges: [
      {
        source: nodes[0].id,
        target: nodes[1].id,
        source_port: 'out',
        target_port: 'in',
      },
      {
        source: nodes[1].id,
        target: nodes[2].id,
        source_port: 'out',
        target_port: 'brief',
      },
      {
        source: nodes[2].id,
        target: nodes[3].id,
        source_port: 'concepts',
        target_port: 'in',
      },
    ],
  };
}

function edgeKey(edge) {
  return `${edge.source}:${edge.source_port || 'out'}→${edge.target}:${edge.target_port || 'in'}`;
}

export function connect(graph, source, target, sourcePort = 'out', targetPort = 'in') {
  if (!source || !target || source === target) throw new Error('Choose two different nodes.');
  const sourceNode = graph.nodes.find((n) => n.id === source);
  const targetNode = graph.nodes.find((n) => n.id === target);
  if (!sourceNode || !targetNode) throw new Error('Unknown node.');
  if (sourceNode.type === 'output' || sourceNode.type === 'export_character') {
    throw new Error('Outputs are endpoints.');
  }
  const srcPorts = defaultPortsForType(sourceNode.type).outputs || [];
  const dstPorts = defaultPortsForType(targetNode.type).inputs || [];
  const sp = srcPorts.find((p) => p.id === sourcePort) || srcPorts[0];
  const tp = dstPorts.find((p) => p.id === targetPort) || dstPorts[0];
  if (!sp || !tp) throw new Error('These nodes have no compatible ports.');
  if (!portsCompatible(sp.type, tp.type)) {
    throw new Error(`Cannot connect ${PORT_TYPES[sp.type]?.label || sp.type} to ${PORT_TYPES[tp.type]?.label || tp.type}.`);
  }
  const nextEdge = {
    source,
    target,
    source_port: sp.id,
    target_port: tp.id,
  };
  if (graph.edges.some((e) => edgeKey(e) === edgeKey(nextEdge))) {
    throw new Error('These ports are already connected.');
  }
  const intoTarget = graph.edges.filter(
    (e) => e.target === target && (e.target_port || 'in') === tp.id
  );
  if (intoTarget.length >= (tp.max || 16)) {
    throw new Error(`${tp.label} already has the maximum number of connections.`);
  }
  const seen = new Set();
  const visit = (id) => {
    if (id === source) return true;
    if (seen.has(id)) return false;
    seen.add(id);
    return graph.edges.filter((e) => e.source === id).some((e) => visit(e.target));
  };
  if (visit(target)) throw new Error('This connection would create a loop. Keep the flow downstream.');
  return { ...graph, edges: [...graph.edges, nextEdge] };
}

export {
  canExecuteCatalogNode,
  characterPickerDefs,
  defaultPortsForType,
  getCatalogDef,
  missingRequiredPorts,
  portsCompatible,
  PORT_TYPES,
  CHARACTER_NODE_CATALOG,
};


/** Materials a combiner/output forwards (aligned with worker bundle inputs). */
export function assembledMaterials(graph, nodeId, records, runsByNode = {}) {
  const seen = new Set();
  const visit = (id) => {
    if (seen.has(id)) return [];
    seen.add(id);
    const node = graph.nodes.find((n) => n.id === id);
    if (!node) return [];
    if (node.type === 'combine' || node.type === 'output') {
      return graph.edges.filter((e) => e.target === id).flatMap((e) => visit(e.source));
    }
    if (node.type === 'image') {
      const prompt = (node.prompt || node.text || '').trim();
      const hasImage = Boolean(node.file_id || node.image);
      return hasImage
        ? [
            {
              kind: 'image',
              label: node.label,
              nodeId: node.id,
              image: node.image || '',
              fileId: node.file_id || '',
              prompt,
              text: prompt,
            },
          ]
        : [];
    }
    if (node.type === 'reference') {
      const record = records.find((r) => r.id === node.reference);
      return [
        {
          kind: 'reference',
          label: node.label,
          nodeId: node.id,
          text: record ? `${record.name}\n${record.brief}` : '',
          ready: Boolean(record),
        },
      ];
    }
    if (isExecutableNode(node.type)) {
      const summary = runsByNode?.[node.id];
      let success = summary?.latest_success;
      const pinnedId = (node.selected_run_id || '').trim();
      if (pinnedId) {
        const fromHistory = (summary?.history || []).find(
          (r) => r.id === pinnedId && r.status === 'succeeded'
        );
        if (fromHistory) success = fromHistory;
        else if (summary?.latest_success?.id === pinnedId) success = summary.latest_success;
      }
      if (!success) {
        return [{ kind: 'pending', label: node.label, nodeId: node.id, ready: false }];
      }
      return [
        {
          kind: 'result',
          label: node.label,
          nodeId: node.id,
          text: success.result?.text || '',
          image: success.result?.image || '',
          artifacts: success.artifacts || [],
          runId: success.id,
          stale: Boolean(success.result_stale),
          ready: !success.result_stale,
          approved: Boolean(node.approved),
        },
      ];
    }
    if (node.type === 'action') {
      return [{ kind: 'pending', label: node.label, nodeId: node.id, ready: false }];
    }
    const text = (node.text || '').trim();
    return text ? [{ kind: 'text', label: node.label, nodeId: node.id, text }] : [];
  };
  return visit(nodeId);
}

/**
 * Incoming materials for a processing node (not the node's own generated output).
 * Matches what the next Run request will send as connected inputs.
 */
export function connectedInputMaterials(graph, nodeId, records, runsByNode = {}) {
  if (!graph?.edges?.length) return [];
  return graph.edges
    .filter((e) => e.target === nodeId)
    .flatMap((e) => assembledMaterials(graph, e.source, records, runsByNode));
}

export function connectedInputText(graph, nodeId, records, runsByNode = {}) {
  return formatMaterialList(connectedInputMaterials(graph, nodeId, records, runsByNode));
}

function formatMaterialList(materials) {
  return materials
    .map((item) => {
      if (item.kind === 'image') {
        return `[Image reference: ${item.label}]\nimage:${item.image.slice(0, 48)}${
          item.image.length > 48 ? '…' : ''
        }`;
      }
      if (item.kind === 'pending') return `[${item.label}: not run yet]`;
      if (item.kind === 'result') {
        const body =
          item.text ||
          (item.image ? `[Image result from ${item.label}]` : '') ||
          (item.artifacts?.length ? `[Artifact: ${item.artifacts[0].name || 'result'}]` : '');
        return item.stale ? `${body}\n[Based on earlier inputs]` : body;
      }
      return item.text || '';
    })
    .filter(Boolean)
    .join('\n\n');
}

// A local assembly preview aligned with worker-resolved materials.
export function assembledText(graph, nodeId, records, runsByNode = {}) {
  return formatMaterialList(assembledMaterials(graph, nodeId, records, runsByNode));
}

/** Merge paginated run history without dropping earlier pages during polling. */
export function mergeRunHistory(existing = [], incoming = []) {
  const byId = new Map();
  [...existing, ...incoming].forEach((run) => {
    if (run?.id) byId.set(run.id, run);
  });
  return Array.from(byId.values()).sort((a, b) => {
    const aTime = Date.parse(a.created_at || a.finished_at || 0) || 0;
    const bTime = Date.parse(b.created_at || b.finished_at || 0) || 0;
    if (bTime !== aTime) return bTime - aTime;
    return (b.attempt_number || 0) - (a.attempt_number || 0);
  });
}

export function downstreamApprovalsInvalidated(graph, changedNodeIds) {
  const seeds = new Set(changedNodeIds || []);
  const affected = new Set(seeds);
  let changed = true;
  while (changed) {
    changed = false;
    for (const edge of graph?.edges || []) {
      if (affected.has(edge.source) && !affected.has(edge.target)) {
        affected.add(edge.target);
        changed = true;
      }
    }
  }
  const nodes = Object.fromEntries((graph?.nodes || []).map((n) => [n.id, n]));
  return [...affected].filter(
    (id) =>
      !seeds.has(id) &&
      nodes[id]?.approved &&
      ['create_images', 'create_3d', 'worker', 'create_ortho', 'texture_pbr', 'rig_skin', 'studio_stage'].includes(
        nodes[id]?.type
      )
  );
}

/** True when changing this image would touch approved downstream work. */
export function imageInputLocked(graph, nodeId) {
  return downstreamApprovalsInvalidated(graph, [nodeId]).length > 0;
}

export function localNodeEstimate(node) {
  const settings = node?.settings && typeof node.settings === 'object' ? node.settings : {};
  return {
    operation: getNodeDef(node?.type)?.operation || node?.type || '',
    settings,
    basis: 'unavailable',
    available: false,
    label_text: 'Estimate unavailable',
    label: 'Estimate unavailable',
    notes:
      'Predicted usage is unavailable — no authoritative production-node rate card is registered. Hovering never starts a run.',
    disclaimer: 'Estimated cost. Actual usage and cost may differ.',
    is_maximum: false,
  };
}

export function formatUsageSummary(usage, { durationMs } = {}) {
  if (!usage || typeof usage !== 'object') return 'Usage unavailable.';
  const basis = usage.basis || 'unavailable';
  const source = usage.source || '';
  const parts = [];
  if (source === 'no_model_call') parts.push('No model call');
  else if (basis === 'simulated') parts.push('Simulated usage');
  else if (basis === 'estimated') parts.push('Estimated');
  else if (basis === 'measured') parts.push('Measured');
  else parts.push('Usage unavailable');

  if (usage.input_tokens != null || usage.output_tokens != null) {
    parts.push(`tokens ${usage.input_tokens ?? '—'}→${usage.output_tokens ?? '—'}`);
  } else if (source === 'unknown' || source === 'unavailable') {
    parts.push('tokens unknown');
  }
  if (usage.provider_credits != null) {
    parts.push(`credits ${usage.provider_credits}${usage.credit_unit ? ` ${usage.credit_unit}` : ''}`);
  }
  if (usage.cost != null && usage.currency) parts.push(`${usage.cost} ${usage.currency}`);
  else if (usage.cost != null) parts.push(`cost ${usage.cost}`);
  else parts.push('Cost unavailable');
  const wall = durationMs != null ? durationMs : usage.duration_ms;
  if (wall != null) parts.push(`wall ${Math.round(wall)} ms`);
  if (usage.billable === false && (basis === 'simulated' || source === 'no_model_call' || usage.notes)) {
    parts.push('no provider charge');
  }
  return parts.join(' · ');
}

export function formatWorkflowTotals(totals) {
  if (!totals) return 'Workflow totals unavailable.';
  const parts = [`${totals.run_count || 0} runs`];
  if (totals.duration_ms?.measured_wall != null) {
    parts.push(`wall ${Math.round(totals.duration_ms.measured_wall)} ms`);
  }
  const renderTokens = (label, bucket) => {
    if (bucket && (bucket.input != null || bucket.output != null)) {
      parts.push(`${label} tokens ${bucket.input ?? '—'}→${bucket.output ?? '—'}`);
    }
  };
  renderTokens('measured', totals.tokens?.measured);
  renderTokens('estimated', totals.tokens?.estimated);
  renderTokens('simulated', totals.tokens?.simulated);
  (totals.credits || []).forEach((row) => {
    parts.push(`${row.basis} ${row.amount} ${row.unit} (${row.provider})`);
  });
  if (totals.cost_available) {
    (totals.costs || []).forEach((row) => {
      parts.push(`${row.basis} ${row.amount} ${row.currency}`);
    });
  } else {
    parts.push('Cost unavailable');
  }
  if (totals.partial) parts.push('partial totals');
  return parts.join(' · ');
}

export function statusLabel(status) {
  return (
    {
      idle: 'Idle',
      queued: 'Queued',
      running: 'Running',
      succeeded: 'Succeeded',
      failed: 'Failed',
      timed_out: 'Timed out',
    }[status] || status || 'Idle'
  );
}

/** Latest attempt status, not “any prior success”. */
export function nodeExecutionState(summary) {
  if (summary?.active_run) return summary.active_run.status;
  const latest = summary?.latest_attempt || summary?.history?.[0];
  if (latest) return latest.status;
  return 'idle';
}

/** Previous successful result retained after a later failed/queued attempt. */
export function retainedSuccessPreview(summary) {
  const success = summary?.latest_success;
  if (!success) return null;
  const latest = summary?.latest_attempt || summary?.history?.[0] || summary?.active_run;
  const retained = Boolean(latest && latest.id !== success.id && latest.status !== 'succeeded');
  return { run: success, retained };
}
